import { FC, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faX } from "@fortawesome/pro-solid-svg-icons";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { useModal } from "../../layout/ModalProvider";
import { VersionHistoryModal } from "./modals/VersionHistoryModal";
import {TeamSpace, TeamSpaceItem} from "../../../api/repositories/documentation-library-repository";
import { DeletePageModal } from "./modals/DeletePageModal";
import { useDocumentationLibraryContext } from "../context/DocumentationLibraryContext";
import { usePermissions } from "@jane/frontend-medewerker/src/resources/PermissionsHook";
import { IconButton } from "../../form/Button";
import { ConfigInterface } from "../../../config";
import {Button} from "@ventumsoftware/react-toolkit";
import {SetDefaultTeamSpacePageModal} from "./modals/SetDefaultTeamSpacePageModal";
import {faClock, faEllipsis, faICursor, faStar, faPencil} from "@fortawesome/pro-regular-svg-icons";
import {faStar as faStarFilled} from "@fortawesome/pro-solid-svg-icons"
import {ContextMenuOption} from "../../content/ContextMenuOption";
import {EditPageModal} from "@jane/frontend-medewerker/src/modals/documentation/EditPageModal";
import {SavePageModal} from "@jane/frontend-medewerker/src/modals/documentation/SavePageModal";

export type DocUser = {
  id: string;
  name: string;
};

export type DocPageVersion = {
  id: string;
  teamSpacePageId: string;
  content: string;
  version: number;
  isCurrentVersion: boolean;
  editedBy: string;
  createdAt: Date;
};

export const DocumentPageHeader: FC<{
  config: ConfigInterface;
  space: TeamSpace;
  page: TeamSpaceItem;
  versions: DocPageVersion[];
  users: DocUser[];
  lastUpdated: Date;
  canDeletePage: boolean;
  onCancel: () => void;
  onFinished: (shouldNotify?: boolean) => void;
  onSpaceUpdated: () => void;
  onDeleted: (page: TeamSpaceItem) => void;
  onPageReverted: (version: DocPageVersion) => void;
}> = (props) => {
  const { deselectPage } = useDocumentationLibraryContext();
  const { canEdit } = useDocumentationLibraryContext();
  return (
    <div className={"flex flex-row justify-between items-center bg-white px-4 py-3 h-20"}>
      <IconButton icon={faArrowLeft} onClick={deselectPage} size={"sm"} type={"white"} />
      <div className={"flex flex-row items-center justify-end space-x-4"}>
        {/*<PresenceIndicator />*/}
        {canEdit(props.space) && <EditButton
          config={props.config}
          page={props.page}
          onCancel={props.onCancel}
          onFinished={props.onFinished}
          onDeleted={props.onDeleted}
        />}
        {canEdit(props.space) && <PageContextMenu
          config={props.config}
          item={props.page}
          versions={props.versions}
          users={props.users}
          onFinished={props.onFinished}
          onDeleted={props.onDeleted}
          onSpaceUpdated={props.onSpaceUpdated}
          onPageReverted={props.onPageReverted}
        />}
      </div>
    </div>
  );
};

const EditButton: FC<{ config: ConfigInterface; page: TeamSpaceItem; onCancel: () => void; onFinished: (shouldNotify?: boolean) => void; onDeleted: (page: TeamSpaceItem) => void }> = (props) => {
  const { editable, setEditable } = useDocumentationLibraryContext();
  const savePageModal = useModal({title: 'Wijzigingen opslaan', body: <SavePageModal setEditable={setEditable} onFinished={props.onFinished}/>, size: 'md'})
  return <div>
    {(editable ? <div className={"flex flex-row space-x-4 items-center"}>
          <Button type={'primary'} size={'sm'} text={'Opslaan'} icon={faSave} onClick={() => savePageModal.open()} />

          <Button type={'secondary'} size={'sm'} text={'Annuleren'} onClick={() => {
            props.onCancel();
            setEditable(false);
          }} />
        </div>
          : <Button type={'primary'} size={'sm'} text={'Bewerken'} icon={faPencil} onClick={() => setEditable(true)} />
      )}
  </div>
};

export const PageContextMenu: FC<{
  config: ConfigInterface
  item: TeamSpaceItem
  versions: DocPageVersion[];
  users: DocUser[];
  onFinished: () => void;
  onDeleted: (page: TeamSpaceItem) => void
  onSpaceUpdated: () => void
  onPageReverted: (version: DocPageVersion) => void
}> = ({ config, item, versions, users, onFinished, onDeleted, onSpaceUpdated, onPageReverted }) => {
  const { canDeleteTeamSpacePage } = usePermissions();
  const { getPages, canEdit, spaces } = useDocumentationLibraryContext();

  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const deletePageModal = useModal({title: "Pagina verwijderen", body: <DeletePageModal config={config} item={item} onDeleted={onDeleted} />, size: "md"});
  const setDefaultModal = useModal({title: "Zet als hoofdpagina", body: <SetDefaultTeamSpacePageModal page={item} getPages={getPages} onSpacesUpdated={onSpaceUpdated} config={config} />, size: 'md'})
  const editPageModal = useModal({title: "Naam aanpassen", body: <EditPageModal item={item} onFinished={() => { onSpaceUpdated(); getPages(item.teamSpaceId) }} />, size:'md'})
  const versionHistoryModal = useModal({
    title: "Versiebeheer",
    body: <VersionHistoryModal config={config} versions={versions} users={users}
        readonly={!canEdit(spaces.find((s) => s.id === item.teamSpaceId)!)}
        onFinished={onFinished} onPageReverted={onPageReverted} />,
    size: "xl",
  })

  return <div className={'relative'}>
    <div className={'flex items-center justify-center px-1.5 py-2 border border-slate-200 rounded-md hover:bg-brand-50'} onClick={() => setIsExpanded(!isExpanded)}>
      <FontAwesomeIcon icon={faEllipsis} />
    </div>
    {isExpanded && <div className={'z-[100] fixed inset-0 bg-[#0002]'} onClick={() => {setIsExpanded(false)}}></div>}
    {isExpanded && <div className={'z-[110] absolute top-11 right-0 min-w-52 min-h-12 bg-white border border-slate-200  rounded-sm text-left'}>
      <ContextMenuOption icon={item.isDefault ? faStarFilled : faStar} text={'Maak hoofdpagina'} close={() => setIsExpanded(false)} onClick={() => setDefaultModal.open()} disabled={item.isDefault} />
      {/*<ContextMenuOption icon={faArrowRightArrowLeft} text={'Verplaats'} close={() => setIsExpanded(false)} onClick={() => {}} />*/}
      <ContextMenuOption icon={faICursor} text={'Naam aanpassen'} close={() => setIsExpanded(false)} onClick={() => editPageModal.open()} />
      <ContextMenuOption icon={faClock} text={'Versies'} close={() => setIsExpanded(false)} onClick={() => versionHistoryModal.open()} />
      {/*<ContextMenuOption icon={faCopy} text={'Maak kopie'} close={() => setIsExpanded(false)} onClick={() => {}} />*/}
      {canDeleteTeamSpacePage && <ContextMenuOption icon={faX} text={'Verwijderen'} close={() => setIsExpanded(false)} onClick={() => deletePageModal.open()}/>}
    </div>}
  </div>
}
